/**
 * 3B · AS CONTAS POR PORTEIRA (apartação: vários destinos na mesma ficha).
 * Sem tela e sem armazenamento, para dar para testar (`testes/contas.mjs`).
 *
 * O gestor contratado compra para um cliente e, no curral, sempre aparece gado
 * fora do critério dele: parte segue para o cliente, parte fica no estoque do
 * gestor, parte já sai vendida. O VENDEDOR recebe um romaneio só, com tudo; o
 * gestor precisa de um fechamento POR PORTEIRA.
 *
 * A REGRA DE OURO CONTINUA: a soma das porteiras é o romaneio do vendedor, ao
 * centavo. Por isso nada aqui é recalculado do zero por porteira — o que o
 * romaneio já arredondou é REPARTIDO:
 *
 *   valor do lote na porteira = o valor do lote no romaneio, repartido pelo
 *                               peso (lote por @ ou por kg) ou pelas cabeças
 *                               (lote por cabeça) que passaram por ela
 *   bruto da porteira         = soma dos lotes dela
 *   financeiro e funrural     = os do romaneio, repartidos pelo bruto (o mesmo
 *                               % da ficha, proporcional). 3D: com descontos
 *                               por lote, cada lote reparte o SEU desconto
 *                               pelo bruto dele em cada porteira
 *   líquido ao vendedor       = bruto − financeiro − funrural
 *
 * Repartir é distribuir CENTAVOS INTEIROS pelo maior resto: cada porteira fica
 * com o valor exato arredondado para baixo e os centavos que sobram vão para
 * quem tinha a maior fração. Dá o mesmo que calcular a porteira sozinha (no
 * máximo um centavo de diferença, quando a fração empata) e a soma bate SEMPRE.
 *
 * Gastos: o lançado numa porteira (o frete de cada caminhão — normalmente são
 * fretes separados, confirmado pelo gestor) fica nela; o lançado sem porteira
 * se divide pelas cabeças, e a tela avisa.
 *
 * Duas comissões, com nomes que não se confundem:
 *  - «a pagar (comprador)» (2D): o gestor paga ao comprador dele, por cabeça.
 *    É custo do gado: entra no custo de cada porteira pelas cabeças dela;
 *  - «a receber (cliente)» (3B): o cliente paga ao gestor, por cabeça da
 *    porteira dele. É RECEITA do gestor, nunca custo do gado.
 * Quem não vê comissão (comprador) recebe as contas SEM nenhuma das duas: os
 * totais dele são outros números, não totais com uma linha escondida.
 */
import { arrobaDoPapel, arrobas, calcular, comissaoTotal, descontosDoLote, gastosValendo, pesoQueVale, ratear, valorDaComissao, type Descontos, type RegraDaArroba, type Resultado } from './contas'
import { contaDaVenda, type ContaDaVenda } from './revenda'
import { NOME_COMISSAO_COMPRADOR, type Comissao, type ComissaoCliente, type Custo, type Ficha, type Lote, type Porteira, type Registro, type VendaDaPorteira } from './tipos'

/* ---------- quem é de qual porteira ---------- */

/** As porteiras da ficha. Ficha antiga (sem o campo) ou sem apartação: lista vazia. */
export function porteirasDa(f: Pick<Ficha, 'porteiras'>): Porteira[] {
  return Array.isArray(f.porteiras) ? f.porteiras.filter((p) => p && typeof p.id === 'string' && p.id) : []
}

/** Duas porteiras ou mais: a tela de pesagem mostra os botões e o Custo, um bloco por porteira. */
export function temApartacao(f: Pick<Ficha, 'porteiras'>): boolean {
  return porteirasDa(f).length > 1
}

/**
 * A porteira de um registro ou gasto. Sem marca, ou com a marca de uma
 * porteira que saiu da lista: a PRIMEIRA — nada fica fora de conta. Ficha sem
 * porteiras: `null` (a porteira única, implícita).
 */
export function porteiraDe(f: Pick<Ficha, 'porteiras'>, destinoId?: string | null): string | null {
  const ps = porteirasDa(f)
  if (!ps.length) return null
  return destinoId && ps.some((p) => p.id === destinoId) ? destinoId : ps[0]!.id
}

export function registrosDaPorteira(f: Pick<Ficha, 'porteiras' | 'registros'>, id: string | null): Registro[] {
  return f.registros.filter((r) => porteiraDe(f, r.destinoId) === id)
}

/** «Cliente · Fazenda Boa Vista» — o nome da porteira nos botões, nas listas e nos papéis. */
export function nomeDaPorteira(p: Porteira | null): string {
  if (!p) return 'Porteira única'
  if (p.tipo === 'estoque') return p.nome?.trim() || 'Meu estoque'
  if (p.tipo === 'revenda') return `Revenda${p.nome?.trim() ? ' · ' + p.nome.trim() : ''}`
  // 3F: cliente é intermediação; o nome continua só o do cliente (é para onde vai o caminhão)
  return p.nome?.trim() || 'Cliente'
}

/* ---------- repartir centavos ---------- */

// 3D: `ratear` mudou para contas.ts (os descontos por lote também repartem);
// continua saindo daqui para quem já a importava deste arquivo.
export { ratear }

const emCentavos = (v: number) => Math.round(v * 100)

/* ---------- a conta de cada porteira ---------- */

/** A arroba de uma parte do lote, com a regra da ficha (R1C). */
function arrobaDaPorteira(kg: number, rendimento: number, f: RegraDaArroba): number {
  const a = arrobas(kg, rendimento)
  return f.arrobaEmDuasCasas === true ? arrobaDoPapel(a) : a
}

export interface LinhaDaPorteira {
  lote: Lote
  registros: number
  cab: number
  kg: number
  cabPesadas: number
  arrobas: number | null
  /** a parte desta porteira no valor do lote do romaneio; nulo quando o lote não tem valor */
  valor: number | null
}

export interface ContaDaPorteira {
  /** nulo: a porteira única, implícita (ficha sem apartação) */
  porteira: Porteira | null
  id: string | null
  nome: string
  linhas: LinhaDaPorteira[]
  cab: number
  kg: number
  cabPesadas: number
  media: number | null
  arrobas: number
  temArroba: boolean
  temPreco: boolean
  bruto: number
  financeiro: number
  funrural: number
  /** 3D · os descontos desta porteira, lote a lote quando são diferentes (a parte dela em cada grupo do romaneio) */
  grupos: (Descontos & { lotes: Lote[]; bruto: number; financeiro: number; funrural: number })[]
  /** todos os lotes da porteira com os mesmos descontos: o papel do cliente mostra o total com o % */
  descontosIguais: boolean
  /** o valor ao vendedor desta porteira — a soma das porteiras é o líquido do romaneio */
  liquido: number
  /** gastos lançados nesta porteira (fretes separados) */
  gastos: Custo[]
  /** a parte desta porteira nos gastos lançados sem porteira (divididos pelas cabeças) */
  gastosRateados: number
  totalGastos: number
  /** «Comissão a pagar (comprador)» desta porteira: por cabeça × cabeças dela. Nulo para quem não vê, ou não lançada. */
  comissaoPagar: { porCabeca: number; cab: number; valor: number; acertada: boolean } | null
  /** «Comissão a receber (cliente)»: só porteira de cliente, só para quem vê comissão, e já lançada */
  comissaoReceber: { porCabeca: number; cab: number; valor: number } | null
  /** porteira de cliente cuja comissão a receber o gestor ainda não lançou (só para quem vê comissão) */
  comissaoReceberPendente: boolean
  /** o que o gado desta porteira custou: vendedor + gastos + comissão a pagar (sem ela, para o comprador) */
  custo: number
  porCabeca: number | null
  porKg: number | null
  porArroba: number | null
  /** cliente: o que o cliente paga — vendedor + gastos + comissão a receber (sem ela, para o comprador) */
  totalDoCliente: number | null
  /**
   * 3F · revenda: a venda ao comprador final (lote a lote, ou o valor total
   * antigo) e o ÁGIO = venda − custo (compra + gastos + comissão a pagar).
   * Nulo para quem não vê (comprador) e para porteira sem venda combinada.
   * `valor`/`agio` nulos: há tabela de venda, mas nenhum lote tem preço ainda.
   */
  venda: { conta: ContaDaVenda; valor: number | null; agio: number | null } | null
  registros: Registro[]
}

export interface Apartacao {
  porteiras: ContaDaPorteira[]
  /** soma dos gastos sem porteira (divididos pelas cabeças): a tela avisa quando há apartação */
  gastosSemPorteira: number
  /** a soma das porteiras bate com o romaneio (bruto, financeiro, funrural, líquido) e com os gastos */
  confere: boolean
  semComissao: boolean
  resultado: Resultado
}

/**
 * As contas de cada porteira. `verComissao` é o papel de quem olha (gestor e
 * Consulta sim, comprador não): sem ele, nenhuma das duas comissões entra,
 * mesmo que alguém as passe.
 */
export function contasPorPorteira(
  f: Pick<Ficha, 'lotes' | 'registros' | 'financeiroPct' | 'funruralPct' | 'custos' | 'porteiras'> & RegraDaArroba,
  o: {
    verComissao: boolean
    comissao?: Pick<Comissao, 'porCabeca' | 'acertoId' | 'cabecas'> | null
    comissaoCliente?: (destinoId: string) => Pick<ComissaoCliente, 'porCabeca'> | null | undefined
    /** 3F · a tabela de venda de cada porteira Revenda (só gestor e Consulta a têm) */
    vendaDe?: (destinoId: string) => Pick<VendaDaPorteira, 'lotes'> | null | undefined
  },
): Apartacao {
  const r = calcular(f)
  const ps = porteirasDa(f)
  const ids: (string | null)[] = ps.length ? ps.map((p) => p.id) : [null]
  const deQual = (destinoId?: string) => porteiraDe(f, destinoId)

  // 1. os lotes: o valor do romaneio repartido pelo peso (ou pelas cabeças) de cada porteira
  const linhas: LinhaDaPorteira[][] = ids.map(() => [])
  for (const l of r.linhas) {
    if (l.cab <= 0) continue
    const porPorteira = ids.map((id) => {
      const regs = f.registros.filter((x) => x.loteId === l.lote.id && !x.pendente && deQual(x.destinoId) === id)
      let cab = 0, kg = 0, cabPesadas = 0
      for (const x of regs) {
        cab += x.qtd
        if (x.kg !== null) { kg += x.kg; cabPesadas += x.qtd }
      }
      return { registros: regs.length, cab, kg, cabPesadas }
    })
    const pesos = porPorteira.map((p) => (l.lote.base === 'cabeca' ? p.cab : p.kg))
    const valores = l.valor === null ? null : ratear(emCentavos(l.valor), pesos)
    porPorteira.forEach((p, i) => {
      if (p.cab <= 0) return
      linhas[i]!.push({
        lote: l.lote, ...p,
        // R1C: a arroba da porteira com a mesma régua do romaneio (quebra antes; ficha nova, 2 casas).
        // O VALOR não sai daqui: é o do romaneio, repartido — a soma continua batendo ao centavo
        arrobas: l.lote.base === 'arroba' ? arrobaDaPorteira(pesoQueVale(p.kg, l.quebraPct), l.lote.rendimento, f) : null,
        valor: valores ? valores[i]! / 100 : null,
      })
    })
  }

  // 2. bruto de cada porteira; financeiro e funrural do romaneio repartidos
  //    pelo bruto. 3D: grupo a grupo — com os descontos iguais é um grupo só
  //    (a conta de sempre); diferentes, cada lote reparte o desconto dele
  const brutos = linhas.map((ls) => ls.reduce((s, l) => s + (l.valor === null ? 0 : emCentavos(l.valor)), 0))
  const fins = ids.map(() => 0)
  const funs = ids.map(() => 0)
  const gruposDe: ContaDaPorteira['grupos'][] = ids.map(() => [])
  for (const g of r.grupos) {
    const deste = new Set(g.lotes.map((l) => l.id))
    const pesos = linhas.map((ls) => ls.reduce((s, l) => s + (deste.has(l.lote.id) && l.valor !== null ? emCentavos(l.valor) : 0), 0))
    const fs = ratear(emCentavos(g.financeiro), pesos)
    const us = ratear(emCentavos(g.funrural), pesos)
    pesos.forEach((p, i) => {
      fins[i]! += fs[i]!
      funs[i]! += us[i]!
      if (p > 0) {
        gruposDe[i]!.push({
          financeiroPct: g.financeiroPct, funruralPct: g.funruralPct, bruto: p / 100, financeiro: fs[i]! / 100, funrural: us[i]! / 100,
          lotes: linhas[i]!.filter((l) => deste.has(l.lote.id)).map((l) => l.lote),
        })
      }
    })
  }
  const cabs = linhas.map((ls) => ls.reduce((s, l) => s + l.cab, 0))

  // 3. gastos: o da porteira fica nela; o sem porteira se divide pelas cabeças.
  //    R2B: o anulado sai; e cada gasto sem porteira se reparte SOZINHO (a mesma
  //    conta de `gastosDoGestor`: o relatório e o Custo dão o mesmo centavo)
  const custos = gastosValendo(f)
  const comPorteira = (c: Custo) => ps.length > 0 && !!c.destinoId && ps.some((p) => p.id === c.destinoId)
  const semPorteira = custos.filter((c) => !comPorteira(c))
  const totalSemPorteira = semPorteira.reduce((s, c) => s + emCentavos(c.valor), 0)
  const rateados = ids.map(() => 0)
  for (const c of semPorteira) ratear(emCentavos(c.valor), cabs).forEach((v, i) => { rateados[i]! += v })
  // R2B: a comissão do comprador é UM valor (o do acerto, com as cabeças do lançamento),
  // repartido pelas cabeças de cada porteira — a soma das porteiras é o valor da ficha
  const comissaoDaFicha = o.verComissao && o.comissao ? valorDaComissao(o.comissao, r.cab) : null
  const comissoesRepartidas = comissaoDaFicha ? ratear(emCentavos(comissaoDaFicha.valor), cabs) : null

  const contas: ContaDaPorteira[] = ids.map((id, i) => {
    const porteira = ps.find((p) => p.id === id) ?? null
    const ls = linhas[i]!
    const cab = cabs[i]!
    const kg = ls.reduce((s, l) => s + l.kg, 0)
    const cabPesadas = ls.reduce((s, l) => s + l.cabPesadas, 0)
    const arr = ls.reduce((s, l) => s + (l.arrobas ?? 0), 0)
    const bruto = brutos[i]!, fin = fins[i]!, fun = funs[i]!
    const liquido = bruto - fin - fun
    const gastos = id === null ? [] : custos.filter((c) => comPorteira(c) && c.destinoId === id).map((c) => ({ ...c }))
    const proprios = id === null ? 0 : gastos.reduce((s, c) => s + emCentavos(c.valor), 0)
    const totalGastos = proprios + rateados[i]!

    const c = o.verComissao && o.comissao ? o.comissao : null
    const comissaoPagar = c && comissoesRepartidas ? { porCabeca: c.porCabeca, cab, valor: comissoesRepartidas[i]! / 100, acertada: !!c.acertoId } : null
    const cliente = porteira?.tipo === 'cliente'
    const cc = cliente && o.verComissao && porteira ? o.comissaoCliente?.(porteira.id) ?? null : null
    const comissaoReceber = cc ? { porCabeca: cc.porCabeca, cab, valor: comissaoTotal(cc.porCabeca, cab) } : null

    const custo = liquido + totalGastos + emCentavos(comissaoPagar?.valor ?? 0)
    const todosPesados = cab > 0 && cabPesadas === cab
    const soArroba = ls.length > 0 && ls.every((l) => l.lote.base === 'arroba')
    // 3F: a venda e o ágio só para quem vê (gestor e Consulta). O comprador
    // não recebe nem a conta: nenhum total dele deixa deduzir o preço de venda
    const cv = o.verComissao && porteira?.tipo === 'revenda'
      ? contaDaVenda(f, porteira, registrosDaPorteira(f, id), o.vendaDe?.(porteira.id) ?? null)
      : null
    const venda = cv ? { conta: cv, valor: cv.valor, agio: cv.valor === null ? null : (emCentavos(cv.valor) - custo) / 100 } : null
    return {
      porteira, id, nome: nomeDaPorteira(porteira), linhas: ls, cab, kg, cabPesadas,
      media: todosPesados ? kg / cabPesadas : null,
      arrobas: arr,
      temArroba: ls.some((l) => l.lote.base === 'arroba'),
      temPreco: ls.some((l) => l.valor !== null),
      bruto: bruto / 100, financeiro: fin / 100, funrural: fun / 100, liquido: liquido / 100,
      grupos: gruposDe[i]!,
      descontosIguais: gruposDe[i]!.every((g) => g.financeiroPct === gruposDe[i]![0]!.financeiroPct && g.funruralPct === gruposDe[i]![0]!.funruralPct),
      gastos, gastosRateados: rateados[i]! / 100, totalGastos: totalGastos / 100,
      comissaoPagar, comissaoReceber,
      comissaoReceberPendente: cliente && o.verComissao && !cc,
      custo: custo / 100,
      porCabeca: cab ? custo / 100 / cab : null,
      porKg: todosPesados && kg ? custo / 100 / kg : null,
      porArroba: soArroba && arr ? custo / 100 / arr : null,
      totalDoCliente: cliente ? (liquido + totalGastos + emCentavos(comissaoReceber?.valor ?? 0)) / 100 : null,
      venda,
      registros: registrosDaPorteira(f, id),
    }
  })

  const soma = (k: 'bruto' | 'financeiro' | 'funrural' | 'liquido' | 'totalGastos') => contas.reduce((s, x) => s + emCentavos(x[k]), 0)
  const confere = soma('bruto') === emCentavos(r.bruto) && soma('financeiro') === emCentavos(r.financeiro)
    && soma('funrural') === emCentavos(r.funrural) && soma('liquido') === emCentavos(r.liquido)
    && soma('totalGastos') === custos.reduce((s, x) => s + emCentavos(x.valor), 0)
    && (!comissaoDaFicha || contas.reduce((s, x) => s + emCentavos(x.comissaoPagar?.valor ?? 0), 0) === emCentavos(comissaoDaFicha.valor))
    && contas.reduce((s, x) => s + x.cab, 0) === r.cab
  return { porteiras: contas, gastosSemPorteira: totalSemPorteira / 100, confere, semComissao: !o.verComissao, resultado: r }
}

/* ---------- a catira e a apartação ---------- */

/**
 * O que a ficha leva para a Catira quando tem porteiras: só a(s) porteira(s)
 * «Meu estoque» — cabeças e custo dela(s), com a comissão a pagar ao comprador
 * (que é custo do gado) e SEM comissão de cliente (que é receita do gestor).
 * Cliente e revenda não são estoque. Ficha sem porteiras: `null` (a Catira
 * continua como antes, com a ficha inteira).
 */
export function estoqueDaFicha(
  f: Pick<Ficha, 'lotes' | 'registros' | 'financeiroPct' | 'funruralPct' | 'custos' | 'porteiras'>,
  comissao: Pick<Comissao, 'porCabeca' | 'acertoId' | 'cabecas'> | null,
): { cabecas: number; valor: number } | null {
  if (!porteirasDa(f).length) return null
  const a = contasPorPorteira(f, { verComissao: true, comissao })
  const est = a.porteiras.filter((p) => p.porteira?.tipo === 'estoque')
  return {
    cabecas: est.reduce((s, p) => s + p.cab, 0),
    valor: est.reduce((s, p) => s + emCentavos(p.custo), 0) / 100,
  }
}

/* ---------- R2D · o estoque por categoria ---------- */

/** Movimento antigo (sem categoria) e lote sem nome de categoria ficam aqui: um estoque só, como antes. */
export const CATEGORIA_GERAL = 'Geral'

/** A categoria de um lote no estoque: o nome do lote, sem espaços sobrando; vazio vira «Geral». */
export function categoriaDoLote(l: Pick<Lote, 'categoria'>): string {
  return String(l.categoria ?? '').trim() || CATEGORIA_GERAL
}

/** O que entra (ou sai) do estoque numa categoria: cabeças, dinheiro, peso e arrobas. */
export interface ParteDaCategoria {
  categoria: string
  cabecas: number
  valor: number
  /** peso da balança; nulo quando algum animal da categoria ficou sem peso */
  kg: number | null
  /** arrobas do romaneio; nulo quando algum lote da categoria não é por arroba */
  arrobas: number | null
}

type LinhaParaRepartir = Pick<LinhaDaPorteira, 'lote' | 'cab' | 'kg' | 'cabPesadas' | 'arrobas' | 'valor'>

/**
 * Reparte, por categoria, o que uma porteira (ou a ficha inteira) leva ao
 * estoque — com a MESMA régua da apartação, para fechar ao centavo:
 *
 *   valor ao vendedor → pelo valor de cada lote JÁ DESCONTADO (bruto do lote
 *                       × (1 − financeiro% − funrural% do lote)); sem preço
 *                       nenhum, pelas cabeças
 *   o resto           → frete, gastos e comissão a pagar, pelas cabeças (como
 *                       destinos.ts reparte gasto sem porteira e a comissão)
 *
 * Os centavos saem de `ratear` (maior resto): a soma das categorias é o total
 * da porteira, sempre. A ordem das categorias é a dos lotes.
 */
export function repartirPorCategoria(
  linhas: LinhaParaRepartir[],
  liquidoCentavos: number,
  restoCentavos: number,
  f: Partial<Pick<Ficha, 'financeiroPct' | 'funruralPct'>>,
): ParteDaCategoria[] {
  const cats: string[] = []
  const grupos = new Map<string, LinhaParaRepartir[]>()
  for (const l of linhas) {
    if (l.cab <= 0) continue
    const k = categoriaDoLote(l.lote)
    if (!grupos.has(k)) { grupos.set(k, []); cats.push(k) }
    grupos.get(k)!.push(l)
  }
  if (!cats.length) return []
  const de = cats.map((k) => grupos.get(k)!)
  const cabs = de.map((ls) => ls.reduce((s, l) => s + l.cab, 0))
  const liquidos = de.map((ls) => ls.reduce((s, l) => {
    if (l.valor === null) return s
    const d = descontosDoLote(l.lote, f)
    return s + emCentavos(l.valor) * (1 - (d.financeiroPct + d.funruralPct) / 100)
  }, 0))
  const pesos = liquidos.some((x) => x > 0) ? liquidos : cabs
  const vend = ratear(liquidoCentavos, pesos)
  const resto = ratear(restoCentavos, cabs)
  return cats.map((categoria, i) => {
    const ls = de[i]!
    const todosPesados = ls.every((l) => l.cabPesadas === l.cab)
    const soArroba = ls.every((l) => l.lote.base === 'arroba' && l.arrobas !== null)
    return {
      categoria, cabecas: cabs[i]!, valor: (vend[i]! + resto[i]!) / 100,
      kg: todosPesados ? Math.round(ls.reduce((s, l) => s + l.kg, 0) * 100) / 100 : null,
      arrobas: soArroba ? Math.round(ls.reduce((s, l) => s + (l.arrobas ?? 0), 0) * 10000) / 10000 : null,
    }
  })
}

/** Soma partes da mesma categoria (duas porteiras «Meu estoque» na mesma ficha). */
function juntarPartes(partes: ParteDaCategoria[]): ParteDaCategoria[] {
  const out: ParteDaCategoria[] = []
  for (const p of partes) {
    const ja = out.find((x) => x.categoria === p.categoria)
    if (!ja) { out.push({ ...p }); continue }
    ja.cabecas += p.cabecas
    ja.valor = (emCentavos(ja.valor) + emCentavos(p.valor)) / 100
    ja.kg = ja.kg === null || p.kg === null ? null : Math.round((ja.kg + p.kg) * 100) / 100
    ja.arrobas = ja.arrobas === null || p.arrobas === null ? null : Math.round((ja.arrobas + p.arrobas) * 10000) / 10000
  }
  return out
}

/**
 * R2D · A compra que entra pela porteira «Meu estoque», separada por
 * categoria conforme os lotes dos registros DESSA porteira. O custo de cada
 * porteira (vendedor + frete e gastos + comissão a pagar; sem comissão de
 * cliente) se reparte pelas categorias com `repartirPorCategoria` — a soma é
 * o `estoqueDaFicha`, ao centavo. Ficha sem porteiras: `null` (quem chama usa
 * o fechamento da ficha inteira, `fechamentoPorCategoria`).
 */
export function estoquePorCategoria(
  f: Pick<Ficha, 'lotes' | 'registros' | 'financeiroPct' | 'funruralPct' | 'custos' | 'porteiras'> & RegraDaArroba,
  comissao: Pick<Comissao, 'porCabeca' | 'acertoId'> | null,
): ParteDaCategoria[] | null {
  if (!porteirasDa(f).length) return null
  const a = contasPorPorteira(f, { verComissao: true, comissao })
  const partes = a.porteiras.filter((p) => p.porteira?.tipo === 'estoque').flatMap((p) =>
    repartirPorCategoria(p.linhas, emCentavos(p.liquido), emCentavos(p.custo) - emCentavos(p.liquido), f))
  return juntarPartes(partes)
}

/**
 * R2D · A ficha inteira por categoria (compra sem porteiras, ou venda): o
 * `total` (custo total da compra, ou o líquido da venda) repartido com a
 * mesma régua — o líquido pelos lotes, o resto pelas cabeças.
 */
export function fichaPorCategoria(
  f: Pick<Ficha, 'lotes' | 'registros' | 'financeiroPct' | 'funruralPct'> & RegraDaArroba,
  total: number,
): ParteDaCategoria[] {
  const r = calcular(f)
  const linhas = r.linhas.map((l) => ({ lote: l.lote, cab: l.cab, kg: l.kg, cabPesadas: l.cabPesadas, arrobas: l.arrobas, valor: l.valor }))
  return repartirPorCategoria(linhas, emCentavos(r.liquido), emCentavos(total) - emCentavos(r.liquido), f)
}

/** Há porteira «Meu estoque» nesta ficha? (sem porteiras, a ficha inteira pode ir para a Catira) */
export function temPorteiraDeEstoque(f: Pick<Ficha, 'porteiras'>): boolean {
  const ps = porteirasDa(f)
  return !ps.length || ps.some((p) => p.tipo === 'estoque')
}

/* ---------- 3F · quem paga o vendedor ---------- */

export interface DivisaoDoPagamento {
  /** o líquido do romaneio inteiro (o que o vendedor recebe, de todos) */
  liquido: number
  /** a parte que o GESTOR paga: porteiras Revenda e Meu estoque (ficha sem porteira: tudo) */
  doGestor: number
  /** a parte que cada cliente (intermediação) paga direto ao vendedor */
  doCliente: { id: string; nome: string; valor: number }[]
  /** soma da parte dos clientes: «a cargo do cliente» */
  aCargoDoCliente: number
}

/**
 * 3F · Na porteira Cliente (intermediação) quem paga ao vendedor a parte
 * daquela porteira é o CLIENTE, direto. O «a pagar» do gestor (Fichas, aba
 * Pagamento, relatório) conta só a parte dele: Revenda, Meu estoque e ficha
 * sem porteira, como sempre. Mesma repartição das porteiras (destinos.ts),
 * então gestor + clientes = líquido do romaneio, ao centavo.
 */
export function divisaoDoPagamento(
  f: Pick<Ficha, 'lotes' | 'registros' | 'financeiroPct' | 'funruralPct' | 'custos' | 'porteiras'> & Partial<Pick<Ficha, 'finalidade'>>,
): DivisaoDoPagamento {
  const r = calcular(f)
  // porteira só existe em ficha de Compra (é onde o gestor aparta)
  const ps = f.finalidade && f.finalidade !== 'Compra' ? [] : porteirasDa(f)
  if (!ps.some((p) => p.tipo === 'cliente')) return { liquido: r.liquido, doGestor: r.liquido, doCliente: [], aCargoDoCliente: 0 }
  const a = contasPorPorteira(f, { verComissao: false })
  const doCliente = a.porteiras.filter((p) => p.porteira?.tipo === 'cliente' && p.id !== null)
    .map((p) => ({ id: p.id!, nome: p.nome, valor: p.liquido }))
  const cliente = doCliente.reduce((s, x) => s + emCentavos(x.valor), 0)
  return { liquido: r.liquido, doGestor: (emCentavos(r.liquido) - cliente) / 100, doCliente, aCargoDoCliente: cliente / 100 }
}

/* ---------- 3F · o bloco da porteira Revenda ---------- */

export interface LinhaDaRevenda {
  rotulo: string
  total: number | null
  porCabeca: number | null
  porArroba: number | null
}

/**
 * As linhas do bloco Revenda (aba Custo e Excel interno, só gestor e
 * Consulta): compra, venda, frete e gastos, comissão a pagar ao comprador e
 * ÁGIO — no total, por cabeça e por arroba. A arroba é a da COMPRA (peso ×
 * rendimento do lote comprado), a mesma régua para todas as linhas, e só
 * quando todos os lotes da porteira são por arroba (regra do custo).
 */
export function linhasDaRevenda(p: ContaDaPorteira): LinhaDaRevenda[] {
  if (p.porteira?.tipo !== 'revenda' || !p.venda) return []
  const soArroba = p.linhas.length > 0 && p.linhas.every((l) => l.lote.base === 'arroba') && p.arrobas > 0
  const linha = (rotulo: string, v: number | null): LinhaDaRevenda => ({
    rotulo, total: v,
    porCabeca: v === null || !p.cab ? null : v / p.cab,
    porArroba: v === null || !soArroba ? null : v / p.arrobas,
  })
  return [
    linha('Compra (valor ao vendedor)', p.liquido),
    linha(p.venda.conta.modo === 'total' ? 'Venda (por valor total)' : 'Venda ao comprador final', p.venda.valor),
    linha('Frete e gastos', p.totalGastos),
    ...(p.comissaoPagar ? [linha(NOME_COMISSAO_COMPRADOR, p.comissaoPagar.valor)] : []),
    linha('Ágio', p.venda.agio),
  ]
}

/**
 * Quanto de cada gasto da ficha (frete, taxa…) é do GESTOR. Na intermediação o
 * cliente paga o gado E o frete direto (decisão do gestor, 06/10/2026: «a
 * responsabilidade é total de quem compra; nem pendência deveria constar»):
 * gasto lançado na porteira Cliente é zero para o gestor, e o gasto sem
 * porteira fica com o gestor só na parte das porteiras que não são de cliente.
 *
 * R2B: a parte sai da MESMA repartição da aba Custo (`ratear` pelas cabeças de
 * cada porteira, gasto a gasto) — antes era uma regra de três arredondada, e o
 * relatório dava um centavo de diferença do Custo. Gasto anulado não entra.
 */
export function gastosDoGestor(f: Pick<Ficha, 'porteiras' | 'registros' | 'custos'> & Partial<Pick<Ficha, 'lotes'>>): Map<string, number> {
  const ps = porteirasDa(f)
  const custos = gastosValendo(f)
  const res = new Map<string, number>()
  const cliente = new Set(ps.filter((p) => p.tipo === 'cliente').map((p) => p.id))
  if (!cliente.size) { custos.forEach((c) => res.set(c.id, c.valor)); return res }
  // as cabeças de cada porteira: registros que valem, de lote que existe (a conta de `calcular`)
  const lotes = Array.isArray(f.lotes) ? new Set(f.lotes.map((l) => l.id)) : null
  const cabs = ps.map(() => 0)
  for (const x of f.registros) {
    if (x.pendente || (lotes && !lotes.has(x.loteId))) continue
    const i = ps.findIndex((p) => p.id === porteiraDe(f, x.destinoId))
    if (i >= 0) cabs[i]! += x.qtd
  }
  for (const c of custos) {
    const naPorteira = !!c.destinoId && ps.some((p) => p.id === c.destinoId)
    if (naPorteira) { res.set(c.id, cliente.has(c.destinoId!) ? 0 : c.valor); continue }
    const partes = ratear(emCentavos(c.valor), cabs)
    res.set(c.id, partes.reduce((s, v, i) => s + (cliente.has(ps[i]!.id) ? 0 : v), 0) / 100)
  }
  return res
}
